import { lazy, Suspense, useState } from "react";

// Static imports for always visible components
import Navbar from "./components/navbar/Navbar";
import LMap from "./components/map/Map";
import Actions from "./components/ui/Actions";
import { useApp, useTenantApp } from "./hooks";

// Lazy imports for conditional components (modals and dialogs)
const RegistrationDialog = lazy(() => import("./components/ui/RegistrationDialog"));
const QRCode = lazy(() => import("./components/qrcode/QRCode"));
const ControlPanelDialog = lazy(() => import("./components/ui/ControlPanelDialog"));

function App() {
    // QR Code Scanner States
    const [showQRScan, setShowQRScan] = useState(false);
    const [showBeaconScan, setShowBeaconScan] = useState(false);
    // QR Code Display State
    const [showQRCode, setShowQRCode] = useState(false);
    // Registration Dialog State
    const [showRegistration, setShowRegistration] = useState(false);
    // State to hold the room ID when joining via QR code
    const [roomToJoin, setRoomToJoin] = useState<string | null>(null);
    // State to hold the BLE device ID when adding a new device
    const [bleDeviceToAdd, setBleDeviceToAdd] = useState<string | null>(null);
    // State for Bottom Sheet Modal
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    // State for POI selection (could be used in ControlPanelDialog)
    const [selectedPOI, setSelectedPOI] = useState<string | null>(null);

    const { app } = useTenantApp();

    const {
        handleLogout,
        handleRegistration,
        handleJoinScannedRoom,
        handleAddBluetoothDevice,
        position,
        loadingOperationOnRoom,
        loadingGeolocation,
        geolocationStatus,
        isWithinBounds,
        roomSession
    } = useApp()

    const handleCloseQRCode = () => {
        setShowQRCode(false);
        setShowQRScan(false);
        setShowBeaconScan(false);
    }

    // TODO: pwa
    // implementare Iconify per ottimizzare le icone e ridurre il bundle size, attualmente stiamo importando tutte le icone di Material Symbols, che sono molte e pesanti.
    return app && (
        <div className="flex flex-col h-screen-safe overflow-hidden">
            <Navbar />

            {(loadingOperationOnRoom || loadingGeolocation) && (
                <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50">
                    <div className="bg-base-100 rounded-full p-1 shadow-lg flex items-center justify-center">
                        <span className="loading loading-spinner text-primary"></span>
                    </div>
                </div>
            )}

            <div className="flex-1 relative">
                <LMap controlPanelPOI={selectedPOI} />
            </div>

            {geolocationStatus && position && isWithinBounds && (
                <Actions
                    login={() => setShowRegistration(true)}
                    sharePosition={() => setShowQRCode(true)}
                    beaconScan={() => setShowBeaconScan(true)}
                    partecipate={() => setShowQRScan(true)}
                    openBottomSheet={() => setShowBottomSheet(true)}
                />
            )}

            <Suspense fallback={null}>
                <RegistrationDialog
                    show={showRegistration}
                    bleDeviceToAdd={bleDeviceToAdd}
                    roomToJoin={roomToJoin}
                    onSubmit={(roomSession) => {
                        if (roomToJoin) {
                            handleJoinScannedRoom({
                                roomId: roomToJoin,
                                username: roomSession.username
                            });
                        } else if (bleDeviceToAdd) {
                            handleAddBluetoothDevice(bleDeviceToAdd, roomSession.username);
                        } else {
                            handleRegistration(roomSession);
                        }
                        setRoomToJoin(null);
                        setBleDeviceToAdd(null);
                        setShowRegistration(false);
                    }}
                    onClose={() => {
                        setRoomToJoin(null);
                        setBleDeviceToAdd(null);
                        setShowRegistration(false);
                    }}
                />

                <QRCode
                    showQRCode={showQRCode}
                    showQRScan={showQRScan}
                    showQRBLEScan={showBeaconScan}
                    onScanSuccess={(result) => {
                        if (showBeaconScan) {
                            setBleDeviceToAdd(result);
                            setShowRegistration(true);
                            setShowBeaconScan(false);
                        } else {
                            setRoomToJoin(result);
                            setShowRegistration(true);
                            setShowQRScan(false);
                        }
                    }}
                    onClose={handleCloseQRCode}
                />

                <ControlPanelDialog
                    show={showBottomSheet}
                    onClose={() => setShowBottomSheet(false)}
                    onLogout={() => handleLogout()}
                    onCreateSession={() => {
                        setShowBottomSheet(false);
                        setShowRegistration(true);
                    }}
                    onScanQr={() => {
                        setShowBottomSheet(false);
                        if (roomSession) {
                            setShowBeaconScan(true);
                        } else {
                            setShowQRScan(true);
                        }
                    }}
                    onPoiSelection={(poi: string) => {
                        setShowBottomSheet(false);
                        setSelectedPOI(poi);
                    }}
                />
            </Suspense>

        </div>
    );
}

export default App;